import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { FIRM } from "./_shared";
import { useTheme } from "../../theme/ThemeProvider";
import { scrollToElement } from "../SmoothScroll";
import CMMark from "./Mark";

// Nav items: `to` is a react-router path; `hash` points to an id on the landing page
const NAV = [
  { label: "Services", to: "/", hash: "practice" },
  { label: "About", to: "/about" },
  { label: "Counsel", to: "/team" },
  { label: "News", to: "/", hash: "insights" },
  { label: "Contact", to: "/contact" },
];

function NavItem({ item, active, className, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e) => {
    if (item.hash) {
      e.preventDefault();
      onNavigate?.();
      if (location.pathname === "/") {
        const el = document.getElementById(item.hash);
        if (el) scrollToElement(el);
        window.history.replaceState(null, "", `/#${item.hash}`);
      } else {
        navigate(`/#${item.hash}`);
      }
    } else {
      onNavigate?.();
    }
  };

  if (item.hash) {
    return (
      <a
        href={`/#${item.hash}`}
        onClick={handleClick}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        data-active={active}
        className={className}
      >
        {item.label}
      </a>
    );
  }
  return (
    <Link
      to={item.to}
      onClick={handleClick}
      data-testid={`nav-link-${item.label.toLowerCase()}`}
      data-active={active}
      className={className}
    >
      {item.label}
    </Link>
  );
}

export default function Header({ transparentOnTop = false }) {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isBlakes = theme === "blakes";
  const overHero = transparentOnTop && !scrolled; // transparent over dark hero

  // Colour scheme
  const logoColor = overHero ? "text-white" : isBlakes ? "text-sage-700" : "text-sage-600";
  const navText = overHero
    ? "text-white/90 hover:text-white"
    : isBlakes
    ? "text-charcoal hover:text-ochre-500"
    : "text-charcoal hover:text-sage-700";
  const ctaBg = isBlakes ? "bg-ochre-500 hover:bg-ochre-600" : "bg-sage-600 hover:bg-sage-700";

  const headerBg = scrolled
    ? isBlakes
      ? "bg-white/92 backdrop-blur-xl border-b border-cream-300"
      : "bg-cream-100/85 backdrop-blur-xl border-b border-cream-300/60"
    : transparentOnTop
    ? "bg-transparent"
    : isBlakes
    ? "bg-white border-b border-cream-300"
    : "bg-cream-100 border-b border-cream-300/60";

  const isActive = (item) => {
    if (item.hash) return false;
    return location.pathname === item.to;
  };

  return (
    <header
      data-testid="site-header"
      data-over-hero={overHero}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${headerBg}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 gap-8">
          {/* Left cluster: monogram + wordmark + nav on same line */}
          <div className="flex items-center min-w-0 flex-1">
            <Link
              to="/"
              data-testid="header-logo-link"
              className="flex items-center group shrink-0"
              aria-label={FIRM.name}
            >
              <CMMark className="h-9 w-9 mr-3 shrink-0 transition-transform duration-500 group-hover:-rotate-6" />
              <span
                className={`font-serif text-xl md:text-2xl lg:text-[26px] tracking-tight leading-none ${logoColor} transition-colors`}
              >
                Charis <span className="italic">&amp;</span> Mettle
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-7 ml-24">
              {NAV.map((item) => (
                <NavItem
                  key={item.label}
                  item={item}
                  active={isActive(item)}
                  className={`nav-link font-sans text-[13px] tracking-wide transition-colors ${navText} data-[active=true]:font-medium`}
                />
              ))}
            </nav>
          </div>

          {/* Right cluster: CTA */}
          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <Link
              to="/intake"
              data-testid="header-cta-button"
              className={`inline-flex items-center gap-2 text-white px-5 py-3 font-sans text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${ctaBg}`}
            >
              Book a consultation
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="header-mobile-toggle"
            aria-label="Toggle menu"
            className={`lg:hidden p-2 -mr-2 ${overHero ? "text-white" : "text-charcoal"}`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-in-out bg-white border-b border-cream-300/60 ${
          open ? "max-h-[560px]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {NAV.map((item) => (
            <NavItem
              key={item.label}
              item={item}
              active={isActive(item)}
              onNavigate={() => setOpen(false)}
              className="font-sans text-base text-charcoal hover:text-ochre-500 transition-colors"
            />
          ))}
          <Link
            to="/intake"
            onClick={() => setOpen(false)}
            data-testid="mobile-cta-button"
            className={`inline-flex items-center justify-center text-white px-5 py-3 mt-2 font-sans text-[11px] uppercase tracking-[0.22em] ${ctaBg}`}
          >
            Book a consultation
          </Link>
        </div>
      </div>
    </header>
  );
}
